<script setup>
import { useForm, usePage, router, Head, Link } from '@inertiajs/vue3';
import { ref, watch, onMounted, nextTick } from 'vue';
import { route } from 'ziggy-js';
import axios from 'axios';

const props = defineProps({
  categories: Array,
  errors: Object,
  flash: Object
});

const page = usePage();
const isEditing = ref(false);

// Helper for soft color
const generateRandomSoftColor = () => {
    const h = Math.floor(Math.random() * 360);
    const s = Math.floor(Math.random() * 30) + 70; // 70-100%
    const l = Math.floor(Math.random() * 20) + 70; // 70-90%
    
    const l_dec = l / 100;
    const a = s * Math.min(l_dec, 1 - l_dec) / 100;
    const f = n => {
        const k = (n + h / 30) % 12;
        const color = l_dec - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
};

// Form Data
const form = useForm({
  hash_id: '',
  name: '',
  slug: '',
  description: '',
  color: generateRandomSoftColor(),
  is_active: true,
  meta_title: '',
  meta_description: ''
});

// Actions
const errorModalContent = ref([]);
const originalData = ref(null);
const modalType = ref('error');
const saving = ref(false);
const settingTitle = ref(page.props.settings?.setting_title || 'GoTransport');

const submit = async () => {
    saving.value = true;

    // Short delay for spinner visibility (like JenisMobil)
    await new Promise(resolve => setTimeout(resolve, 300));

    // Client-side validation for required fields
    const errors = [];
    if (!form.name || form.name.trim() === '') {
        errors.push('Nama kategori wajib diisi.');
    }
    if (!form.color || form.color.trim() === '') {
        errors.push('Warna kategori wajib dipilih.');
    }
    
    if (errors.length > 0) {
        saving.value = false;
        errorModalContent.value = errors;
        modalType.value = 'error';
        const modal = new bootstrap.Modal(document.getElementById('errorModal'));
        modal.show();
        return;
    }
    
    // Check for changes if editing
    if (isEditing.value) {
        const currentData = {
            name: form.name,
            slug: form.slug,
            description: form.description || '',
            color: form.color,
            is_active: form.is_active,
            meta_title: form.meta_title || '',
            meta_description: form.meta_description || ''
        };
        
        if (JSON.stringify(currentData) === JSON.stringify(originalData.value)) {
            saving.value = false;
            errorModalContent.value = ['Tidak ada perubahan data yang disimpan.'];
            modalType.value = 'warning';
            const modal = new bootstrap.Modal(document.getElementById('errorModal'));
            modal.show();
            return;
        }
    }
    
    // saving.value is already true from the start
    
    const options = {
        preserveScroll: true,
        onSuccess: () => {
            saving.value = false;
            resetForm();
        },
        onError: (errors) => {
            saving.value = false;
            errorModalContent.value = Object.values(errors).flat();
            modalType.value = 'error';
            const modal = new bootstrap.Modal(document.getElementById('errorModal'));
            modal.show();
        },
        onFinish: () => {
            saving.value = false;
        }
    };
    
    if (isEditing.value) {
        form.put(`/admin/category/${form.hash_id}`, options);
    } else {
        form.post(route('admin.category.store'), options);
    }
};

const edit = (item) => {
    isEditing.value = true;
    form.hash_id = item.hash_id;
    form.name = item.name;
    form.slug = item.slug || '';
    form.description = item.description || '';
    form.color = item.color || '#007bff';
    form.is_active = item.is_active ? true : false;
    form.meta_title = item.meta_title || generateMetaTitle(item.name);
    // Fallback for description: use item description, OR generate from name if description is empty
    if (item.meta_description) {
        form.meta_description = item.meta_description;
    } else if (item.description) {
        form.meta_description = generateMetaDescription(item.description);
    } else {
        form.meta_description = generateMetaDescriptionFromName(item.name);
    }
    
    // Store original data from database
    originalData.value = {
        name: item.name,
        slug: item.slug || '',
        description: item.description || '',
        color: item.color || '#007bff',
        is_active: item.is_active ? true : false,
        meta_title: item.meta_title || '',
        meta_description: item.meta_description || ''
    };
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

const deleteForm = useForm({});
const deleteId = ref(null);
const deleteName = ref('');
const deleteConfirmedCheck = ref(false);

const confirmDelete = (item) => {
    deleteId.value = item.id;
    deleteName.value = item.name;
    deleteConfirmedCheck.value = false;
    const modal = new bootstrap.Modal(document.getElementById('deleteModal'));
    modal.show();
};

const submitDelete = () => {
    if (!deleteId.value) return;
    deleteForm.delete(route('admin.category.destroy', deleteId.value), {
        preserveScroll: true,
        onSuccess: () => {
            const modalEl = document.getElementById('deleteModal');
            const modal = bootstrap.Modal.getInstance(modalEl);
            if (modal) modal.hide();
        }
    });
};

const processingStatusId = ref(null);
const toggleStatus = async (item) => {
    if (!item.hash_id) {
        errorModalContent.value = ['ID tidak ditemukan. Silakan refresh halaman.'];
        modalType.value = 'error';
        const modal = new bootstrap.Modal(document.getElementById('errorModal'));
        modal.show();
        return;
    }
    
    processingStatusId.value = item.hash_id;
    try {
        const response = await axios.patch(`/admin/category/${item.hash_id}/toggle-status`);
        if (response.data.status === 'success' || response.data.is_active !== undefined) {
            item.is_active = response.data.is_active ?? !item.is_active;
        }
    } catch (error) {
        console.error('Toggle status error:', error);
        errorModalContent.value = ['Gagal mengubah status.'];
        modalType.value = 'error';
        const modal = new bootstrap.Modal(document.getElementById('errorModal'));
        modal.show();
    } finally {
        processingStatusId.value = null;
    }
};

// Slug and Meta Logic
const slugify = (text) => {
  return text.toString().toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w\-]+/g, '').replace(/\-\-+/g, '-').replace(/^-+/, '').replace(/-+$/, '');
};

const generateMetaTitle = (name) => {
    // Priority: "[Name] - Sewa Mobil Banyuwangi"
    let title = `${name} - Sewa Mobil Banyuwangi`;
    
    // If we have space, add site title: "[Name] - Sewa Mobil Banyuwangi | [SiteTitle]"
    if (title.length + settingTitle.value.length + 3 <= 55) {
        title = `${title} | ${settingTitle.value}`;
    }

    if (title.length <= 55) {
        return title;
    }
    
    // If too long, try just "[Name] - Sewa Mobil Banyuwangi" (truncate if needed)
    if (title.length > 55) {
         // Try to keep "Sewa Mobil Banyuwangi" if possible, but Name is priority
         // If generic title is too long, fall back to just Name | SiteTitle
         const backupTitle = `${name} | ${settingTitle.value}`;
         if (backupTitle.length <= 55) return backupTitle;
         
         return name.substring(0, 52) + '...';
    }
    return title;
};

const generateMetaDescription = (description) => {
    // If description is provided manually (and not empty), use it
    if (description) {
        let plainText = description.replace(/<[^>]*>?/gm, '');
        if (plainText.length <= 155) return plainText;
        return plainText.substring(0, 152) + '...';
    }
    return ''; 
};

// New helper for generating description from Name
const generateMetaDescriptionFromName = (name) => {
    const text = `Informasi terbaru seputar ${name}. Layanan Sewa Mobil Banyuwangi terbaik, murah, dan terpercaya.`;
    if (text.length <= 155) return text;
    return text.substring(0, 152) + '...';
};

watch(() => form.name, (newValue) => {
    if (!isEditing.value && newValue) {
         form.slug = slugify(newValue);
    }
    if (newValue && (!isEditing.value || form.meta_title === '')) {
        form.meta_title = generateMetaTitle(newValue);
    }
    // Auto-generate meta_description from name if description is empty
    if (newValue && (!isEditing.value || form.meta_description === '') && !form.description) {
         form.meta_description = generateMetaDescriptionFromName(newValue);
    }
});

watch(() => form.description, (newValue) => {
    if (newValue && (!isEditing.value || form.meta_description === '')) {
        form.meta_description = generateMetaDescription(newValue);
    } else if (!newValue && form.name) {
        // If description cleared, fall back to name-based meta
         form.meta_description = generateMetaDescriptionFromName(form.name);
    }
});

const resetForm = () => {
    isEditing.value = false;
    originalData.value = null;
    form.reset();
    form.clearErrors();
    form.color = generateRandomSoftColor();
    form.is_active = true;
    form.meta_title = '';
    form.meta_description = '';
};

onMounted(() => {
    // Show flash error if present
    if (page.props.flash?.error) {
        errorModalContent.value = [page.props.flash.error];
        modalType.value = 'error';
        nextTick(() => {
            const modal = new bootstrap.Modal(document.getElementById('errorModal'));
            modal.show();
        });
    }
});
</script>

<template>
    <Head title="Kategori Artikel" />
    <div class="container-full">
        <!-- Breadcrumb Section -->
        <div class="content-header">
            <div class="d-flex align-items-center justify-content-between">
                <div class="d-block">
                    <h4 class="page-title">Kelola Kategori Artikel</h4>
                    <div class="d-inline-block align-items-center">
                        <nav>
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item"><Link href="/admin/dashboard"><i class="mdi mdi-home-outline"></i></Link></li>
                                <li class="breadcrumb-item active" aria-current="page">Kategori Artikel</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </div>
        </div>

        <section class="content">
            <!-- Form Section -->
            <div class="row">
                <!-- Form Section -->
                <div class="col-lg-5 col-12 mb-4">
                    <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title text-primary">
                                <i class="ti-plus me-2" v-if="!isEditing"></i>
                                <i class="ti-pencil me-2" v-else></i>
                                {{ isEditing ? 'Edit Kategori' : 'Tambah Kategori' }}
                            </h4>
                        </div>
                        <form @submit.prevent="submit">
                            <div class="box-body">
                                <div class="form-group mb-3">
                                    <label for="name" class="form-label">Nama Kategori <span class="text-danger">*</span></label>
                                    <input type="text" id="name" name="name" v-model="form.name" class="form-control" :class="{ 'is-invalid': form.errors.name }" placeholder="Nama kategori" autocomplete="off">
                                    <div class="invalid-feedback">{{ form.errors.name }}</div>
                                </div>

                                <div class="form-group mb-3">
                                    <label for="slug" class="form-label">Slug</label>
                                    <input type="text" id="slug" name="slug" v-model="form.slug" class="form-control" :class="{ 'is-invalid': form.errors.slug }" autocomplete="off">
                                    <div class="invalid-feedback">{{ form.errors.slug }}</div>
                                </div>

                                <div class="form-group mb-3">
                                    <label for="description" class="form-label">Deskripsi</label>
                                    <textarea id="description" name="description" v-model="form.description" class="form-control" rows="3" :class="{ 'is-invalid': form.errors.description }" placeholder="Deskripsi singkat kategori" autocomplete="off"></textarea>
                                    <div class="invalid-feedback">{{ form.errors.description }}</div>
                                </div>

                                <div class="form-group mb-3">
                                    <label for="color" class="form-label">Warna <span class="text-danger">*</span></label>
                                    <div class="input-group">
                                        <input type="color" id="color" name="color" v-model="form.color" class="form-control form-control-color" style="width: 60px;">
                                        <input type="text" id="color_text" name="color_text" v-model="form.color" class="form-control" :class="{ 'is-invalid': form.errors.color }" placeholder="#007bff" autocomplete="off">
                                    </div>
                                    <div class="invalid-feedback d-block" v-if="form.errors.color">{{ form.errors.color }}</div>
                                </div>

                                <!-- SEO Meta Data -->
                                <div class="card bg-light-light mb-0 mt-3">
                                    <div class="card-body py-3">
                                        <h5 class="text-primary mb-3"><i class="ti-world me-2"></i>SEO Meta Data</h5>

                                        <div class="form-group mb-3">
                                            <div class="d-flex justify-content-between">
                                                <label for="meta_title" class="form-label">Meta Title</label>
                                                <small :class="form.meta_title?.length > 55 ? 'text-danger' : 'text-muted'">{{ form.meta_title?.length || 0 }}/55</small>
                                            </div>
                                            <input type="text" id="meta_title" name="meta_title" v-model="form.meta_title" class="form-control" :class="{ 'is-invalid': form.errors.meta_title }" placeholder="Meta Title" autocomplete="off">
                                            <div class="invalid-feedback">{{ form.errors.meta_title }}</div>
                                            <small class="text-muted d-block mt-1">Judul yang akan muncul di hasil pencarian Google.</small>
                                        </div>

                                        <div class="form-group mb-0">
                                            <div class="d-flex justify-content-between">
                                                <label for="meta_description" class="form-label">Meta Description</label>
                                                <small :class="form.meta_description?.length > 155 ? 'text-danger' : 'text-muted'">{{ form.meta_description?.length || 0 }}/155</small>
                                            </div>
                                            <textarea id="meta_description" name="meta_description" v-model="form.meta_description" class="form-control" rows="3" :class="{ 'is-invalid': form.errors.meta_description }" placeholder="Meta Description" autocomplete="off"></textarea>
                                            <div class="invalid-feedback">{{ form.errors.meta_description }}</div>
                                            <small class="text-muted d-block mt-1">Deskripsi singkat yang muncul di bawah judul pada hasil pencarian.</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="box-footer text-end">
                                <button v-if="isEditing" type="button" class="btn btn-warning me-2" @click="resetForm" :disabled="saving">
                                    <i class="ti-trash me-2"></i> Batal
                                </button>
                                <button type="submit" class="btn btn-primary" :disabled="saving">
                                    <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
                                    <i v-else class="ti-save-alt me-2"></i>
                                    {{ saving ? 'Menyimpan...' : (isEditing ? 'Simpan Perubahan' : 'Simpan') }}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                <!-- List Section -->
                <div class="col-lg-7 col-12">
                     <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title text-primary"><i class="ti-view-list me-2"></i>Daftar Kategori</h4>
                        </div>
                        <div class="box-body">
                            <div class="table-responsive">
                                <table class="table table-hover mb-0">
                                    <thead>
                                        <tr>
                                            <th>Nama</th>
                                            <th>Deskripsi</th>
                                            <th>Warna</th>
                                            <th>Artikel</th>
                                            <th>Status</th>
                                            <th class="text-center">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                         <tr v-if="categories.length === 0">
                                            <td colspan="5" class="text-center">Belum ada data kategori.</td>
                                        </tr>
                                        <tr v-for="item in categories" :key="item.id" class="align-middle">
                                            <td>
                                                <div class="fw-bold">{{ item.name }}</div>
                                                <div class="small text-muted">/{{ item.slug }}</div>
                                            </td>
                                            <td>
                                                <small class="text-muted" v-if="item.description">{{ item.description.substring(0, 50) }}{{ item.description.length > 50 ? '...' : '' }}</small>
                                                <span v-else class="text-muted">-</span>
                                            </td>
                                            <td>
                                                <span class="badge" :style="{ backgroundColor: item.color, color: '#fff' }">{{ item.color }}</span>
                                            </td>
                                            <td>
                                                <span class="badge bg-info">{{ item.news_count || 0 }}</span>
                                            </td>
                                            <td>
                                                 <button type="button" class="btn btn-sm rounded-1 px-3" :class="item.is_active ? 'btn-success' : 'btn-danger'" @click="toggleStatus(item)" :disabled="processingStatusId === item.hash_id" title="Toggle Status">
                                                     <span v-if="processingStatusId === item.hash_id" class="spinner-border spinner-border-sm me-1"></span>
                                                     <i v-else class="fa me-1" :class="item.is_active ? 'fa-check-circle' : 'fa-times-circle'"></i>
                                                     {{ item.is_active ? 'Aktif' : 'Nonaktif' }}
                                                 </button>
                                            </td>
                                            <td class="text-center" style="width: 120px;">
                                                <div class="btn-group">
                                                     <button class="btn btn-primary btn-sm" @click="edit(item)" title="Ubah"><i class="fa fa-edit"></i></button>
                                                     <button class="btn btn-danger btn-sm" @click="confirmDelete(item)" title="Hapus"><i class="fa fa-trash"></i></button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        <!-- Delete Modal -->
        <div class="modal fade" id="deleteModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header bg-danger text-white">
                        <h5 class="modal-title"><i class="ti-alert me-2"></i>Konfirmasi Hapus</h5>
                        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p>Apakah Anda yakin ingin menghapus kategori <strong>{{ deleteName }}</strong>?</p>
                        <p class="text-danger small mb-3">Tindakan ini tidak dapat dibatalkan.</p>
                        
                        <div class="form-check list-group-item list-group-item-danger p-3 rounded mb-0">
                            <input class="form-check-input" type="checkbox" id="confirmCheck" v-model="deleteConfirmedCheck">
                            <label class="form-check-label text-dark" for="confirmCheck">
                                Saya mengerti dan yakin ingin menghapus data ini.
                            </label>
                        </div>
                    </div>
                    <div class="modal-footer text-end">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"><i class="fa fa-times me-2"></i>Batal</button>
                        <button type="button" class="btn btn-danger" :disabled="!deleteConfirmedCheck" @click="submitDelete">
                            <i class="fa fa-trash me-2"></i>Ya, Hapus
                        </button>
                    </div>
                </div>
            </div>
        </div>
        
         <!-- Error/Warning Modal -->
        <div class="modal fade" id="errorModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header text-white" :class="modalType === 'warning' ? 'bg-warning text-dark' : 'bg-danger'">
                        <h5 class="modal-title">
                            <i class="me-2" :class="modalType === 'warning' ? 'fa fa-exclamation-triangle' : 'ti-alert'"></i>
                            {{ modalType === 'warning' ? 'Peringatan' : 'Peringatan Validasi' }}
                        </h5>
                        <button type="button" class="btn-close" :class="modalType === 'warning' ? '' : 'btn-close-white'" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p class="mb-3" v-if="modalType === 'error'">Mohon perbaiki kesalahan berikut sebelum menyimpan:</p>
                        <p class="mb-3" v-else>Informasi sistem:</p>
                        <ul class="list-group list-group-flush">
                            <li class="list-group-item" :class="modalType === 'warning' ? 'list-group-item-warning' : 'list-group-item-danger'" v-for="(error, index) in errorModalContent" :key="index">
                                <i class="fa me-2" :class="modalType === 'warning' ? 'fa-info-circle' : 'fa-exclamation-circle'"></i>{{ error }}
                            </li>
                        </ul>
                    </div>
                    <div class="modal-footer text-end">
                        <button type="button" class="btn" :class="modalType === 'warning' ? 'btn-warning' : 'btn-danger'" data-bs-dismiss="modal"><i class="fa fa-check-square-o me-2"></i>Mengerti</button>
                    </div>
                </div>
            </div>
        </div>

        </section>
    </div>
</template>
